import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';
import BioSleeveViewer from '../components/BioSleeveViewer';

const API = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: 'easeOut' } }),
};

const statusColors = {
  research: 'status-research',
  prototype: 'status-prototype',
  development: 'status-development',
  deployed: 'status-deployed',
};

const statusLabel = {
  research: 'In Research',
  prototype: 'Prototype',
  development: 'In Development',
  deployed: 'Deployed',
};

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/projects`)
      .then((r) => setProjects(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="pt-24">
      {/* ── Page Hero ── */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 70% 50% at 90% 30%, rgba(57,255,20,0.07) 0%, transparent 60%)' }}
        />
        <div className="max-w-7xl relative z-10">
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            <span className="section-label">Our Innovations</span>
          </motion.div>
          <motion.h1
            variants={fadeUp} initial="hidden" animate="visible" custom={1}
            className="font-outfit font-bold text-5xl sm:text-6xl text-white mt-6 mb-6 leading-tight"
            data-testid="projects-heading"
          >
            Projects &{' '}
            <span className="text-gradient-green">Products</span>
          </motion.h1>
          <motion.p
            variants={fadeUp} initial="hidden" animate="visible" custom={2}
            className="text-white/55 font-inter-tight text-lg max-w-2xl leading-relaxed"
          >
            From wearable neural interfaces to molecular nanotechnology — these are our core innovations, each designed to solve a real and urgent human problem.
          </motion.p>
        </div>
      </section>

      {/* ── Projects List ── */}
      <section className="px-6 md:px-12 lg:px-24 pb-24 border-t border-bvx-border">
        <div className="max-w-7xl">
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <Loader2 className="text-bvx-cyan animate-spin" size={32} />
            </div>
          ) : (
            <div className="space-y-0">
              {projects.map((project, i) => (
                <motion.article
                  key={project.id}
                  variants={fadeUp} initial="hidden" whileInView="visible" custom={i * 0.1} viewport={{ once: true }}
                  className={`py-16 ${i > 0 ? 'border-t border-bvx-border' : 'pt-16'}`}
                  data-testid={`project-${project.slug}`}
                >
                  <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                    {/* Image */}
                    {project.image_url ? (
                      <div className={`order-1 ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                        <div className="relative rounded-2xl overflow-hidden border border-bvx-border">
                          <img
                            src={project.image_url}
                            alt={project.name}
                            className="w-full h-72 md:h-96 object-cover opacity-80"
                          />
                          <div
                            className="absolute inset-0"
                            style={{ background: `linear-gradient(to top, rgba(5,5,5,0.7) 0%, transparent 50%)` }}
                          />
                          <div className="absolute bottom-5 left-5">
                            <span className={`status-badge ${statusColors[project.status] || 'status-research'}`}>
                              {statusLabel[project.status] || project.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className={`order-1 ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                        <div className="glass-card h-72 md:h-96 flex items-center justify-center">
                          <span className="font-outfit font-bold text-6xl text-white/10">{project.name[0]}</span>
                        </div>
                      </div>
                    )}

                    {/* Content */}
                    <div className={`order-2 ${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                      <span className={`status-badge ${statusColors[project.status] || 'status-research'} mb-4 inline-block`}>
                        {statusLabel[project.status] || project.status}
                      </span>
                      <h2 className="font-outfit font-bold text-white text-4xl sm:text-5xl mb-3">
                        {project.name}
                      </h2>
                      <p className="text-bvx-cyan font-inter-tight text-base mb-5">{project.tagline}</p>

                      <div className="space-y-3 mb-6">
                        {project.description.split('\n').filter(Boolean).map((line, li) => {
                          if (line.startsWith('•')) {
                            return (
                              <div key={li} className="flex items-start gap-2">
                                <CheckCircle2 size={14} className="text-bvx-green shrink-0 mt-0.5" />
                                <p className="text-white/55 font-inter-tight text-sm leading-relaxed">{line.slice(1).trim()}</p>
                              </div>
                            );
                          }
                          return (
                            <p key={li} className="text-white/55 font-inter-tight text-sm leading-relaxed">{line}</p>
                          );
                        })}
                      </div>

                      <div className="flex flex-wrap gap-2 mb-8">
                        {project.tags.map((tag) => (
                          <span key={tag} className="text-xs text-white/40 bg-white/5 border border-white/8 px-3 py-1 rounded-full font-inter-tight">
                            {tag}
                          </span>
                        ))}
                      </div>

                      <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid={`project-cta-${project.slug}`}>
                        Collaborate on This <ArrowRight size={15} />
                      </Link>
                    </div>
                  </div>

                  {/* BioSleeve 3D Viewer */}
                  {project.slug === 'biosleeve' && <BioSleeveViewer />}
                </motion.article>
              ))}
            </div>
          )}

          {/* Coming Soon */}
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="mt-16 glass-card p-10 text-center border-dashed"
            data-testid="coming-soon"
          >
            <div
              className="inline-block w-14 h-14 rounded-2xl bg-bvx-cyan/10 border border-bvx-cyan/20 flex items-center justify-center mx-auto mb-5"
            >
              <span className="text-2xl">+</span>
            </div>
            <h3 className="font-outfit font-bold text-white text-2xl mb-3">More Coming Soon</h3>
            <p className="text-white/45 font-inter-tight text-sm max-w-md mx-auto leading-relaxed mb-6">
              Our research pipeline includes several more innovations currently in early-stage development. Stay connected for announcements.
            </p>
            <Link to="/research" className="btn-outline inline-flex items-center gap-2" data-testid="follow-research-btn">
              Follow Our Research <ArrowRight size={15} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
